import { useEffect, useMemo, useState } from "react";
import {
  getInsightsMemberJourney,
  getInsightsSummary,
  getResourceSummary,
  listInsightsMembers,
  listChapters,
  listEventAttendance,
  listEventRegistrations,
  listEvents,
  listMembers,
  listResourceBlocks,
  listResourceReservations
} from "../lib/rangegridApi";

const ORGANIZATION_INSIGHT_TABS = [
  { id: "trends", label: "Trends" },
  { id: "chapter-journey", label: "Chapter Journey" },
  { id: "member-journey", label: "Member Journey" }
];

const RANGE_INSIGHT_TABS = [
  { id: "retention", label: "Retention" },
  { id: "usage", label: "Usage" },
  { id: "range-journey", label: "Range Journey" },
  { id: "member-journey", label: "Member Journey" }
];

const PRESET_WINDOWS = [
  { label: "Last 90 days", days: 90 },
  { label: "Last 6 months", days: 180 },
  { label: "Last year", days: 365 },
  { label: "Custom", days: 0 }
];

const AGGREGATE_WINDOWS = [
  { label: "Auto", value: "auto" },
  { label: "Day", value: "day" },
  { label: "Month", value: "month" },
  { label: "Quarter", value: "quarter" },
  { label: "Year", value: "year" }
];

const INTERNAL_DEMO_EVENT_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|test event|audit)/i;

function parseDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateInputValue(date) {
  if (!date) {
    return "";
  }
  const value = date instanceof Date ? date : parseDate(date);
  if (!value) {
    return "";
  }
  return value.toISOString().slice(0, 10);
}

function startOfDay(value) {
  const date = value instanceof Date ? new Date(value) : parseDate(value);
  if (!date) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(value) {
  const date = value instanceof Date ? new Date(value) : parseDate(value);
  if (!date) {
    return null;
  }
  date.setHours(23, 59, 59, 999);
  return date;
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return startOfDay(date);
}

function formatDate(value, options = {}) {
  const date = parseDate(value);
  if (!date) {
    return "Not recorded";
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(options.year === false ? {} : { year: "numeric" })
  });
}

function formatTime(value) {
  const date = parseDate(value);
  if (!date) {
    return "";
  }
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatEventWindow(event = {}) {
  const date = formatDate(event.startsAt);
  const start = formatTime(event.startsAt);
  const end = formatTime(event.endsAt);
  return [date, [start, end].filter(Boolean).join(" - ")].filter(Boolean).join(" / ");
}

function monthSpan(start, end) {
  const first = parseDate(start);
  const last = parseDate(end);
  if (!first || !last) {
    return 0;
  }
  return ((last.getFullYear() - first.getFullYear()) * 12) + (last.getMonth() - first.getMonth()) + 1;
}

function daySpan(start, end) {
  const first = startOfDay(start);
  const last = startOfDay(end);
  if (!first || !last) {
    return 0;
  }
  return Math.floor((last.getTime() - first.getTime()) / 86400000) + 1;
}

function bucketGranularity(start, end, requested = "auto") {
  if (["day", "month", "quarter", "year"].includes(requested)) {
    return requested;
  }
  if (daySpan(start, end) <= 14) {
    return "day";
  }
  const months = monthSpan(start, end);
  if (months <= 8) {
    return "month";
  }
  if (months <= 36) {
    return "quarter";
  }
  return "year";
}

function bucketKey(value, granularity = "month") {
  const date = parseDate(value);
  if (!date) {
    return "";
  }
  if (granularity === "day") {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  if (granularity === "year") {
    return String(date.getFullYear());
  }
  if (granularity === "quarter") {
    return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function bucketLabel(key, granularity = "month") {
  if (granularity === "day") {
    const [year, month, day] = String(key || "").split("-");
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  if (granularity === "year") {
    return String(key || "");
  }
  if (granularity === "quarter") {
    const [year, quarter] = String(key || "").split("-Q");
    return year && quarter ? `Q${quarter} ${year}` : "";
  }
  const [year, month] = String(key || "").split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function getTimeBuckets(start, end, requestedGranularity = "auto") {
  const first = startOfDay(start);
  const last = endOfDay(end);
  if (!first || !last) {
    return [];
  }
  const granularity = bucketGranularity(first, last, requestedGranularity);
  const cursor = granularity === "day"
    ? new Date(first.getFullYear(), first.getMonth(), first.getDate())
    : granularity === "year"
    ? new Date(first.getFullYear(), 0, 1)
    : granularity === "quarter"
      ? new Date(first.getFullYear(), Math.floor(first.getMonth() / 3) * 3, 1)
      : new Date(first.getFullYear(), first.getMonth(), 1);
  const limit = granularity === "day"
    ? new Date(last.getFullYear(), last.getMonth(), last.getDate())
    : granularity === "year"
    ? new Date(last.getFullYear(), 0, 1)
    : granularity === "quarter"
      ? new Date(last.getFullYear(), Math.floor(last.getMonth() / 3) * 3, 1)
      : new Date(last.getFullYear(), last.getMonth(), 1);
  const buckets = [];
  while (cursor <= limit) {
    const key = bucketKey(cursor, granularity);
    buckets.push({ key, label: bucketLabel(key, granularity), granularity });
    if (granularity === "day") {
      cursor.setDate(cursor.getDate() + 1);
    } else {
      cursor.setMonth(cursor.getMonth() + (granularity === "year" ? 12 : granularity === "quarter" ? 3 : 1));
    }
  }
  return buckets;
}

function eventChapterIds(event = {}) {
  return Array.isArray(event.chapterIds)
    ? event.chapterIds.filter(Boolean)
    : event.chapterId
      ? [event.chapterId]
      : [];
}

function isChapterOrganizationRange(range = {}) {
  return (
    range?.config?.templateKey === "chapter_organization" ||
    range?.creationTemplate?.templateKey === "chapter_organization" ||
    range?.templateKey === "chapter_organization" ||
    String(range?.config?.vocabulary?.unit || range?.creationTemplate?.vocabulary?.unit || range?.vocabulary?.unit || "").toLowerCase() === "chapter"
  );
}

function memberChapterIds(member = {}) {
  return [
    member.homeChapterId,
    member.chapterId,
    member.chapterSnapshot?.chapterId,
    ...(Array.isArray(member.chapterIds) ? member.chapterIds : [])
  ]
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean);
}

function memberMatchesChapter(member, selectedChapterIds) {
  if (!selectedChapterIds.length) {
    return true;
  }
  const ids = memberChapterIds(member);
  return ids.some((chapterId) => selectedChapterIds.includes(chapterId));
}

function eventMatchesChapter(event, selectedChapterIds) {
  if (!selectedChapterIds.length) {
    return true;
  }
  const ids = eventChapterIds(event);
  return ids.some((chapterId) => selectedChapterIds.includes(chapterId));
}

function memberName(member = {}) {
  return String(member.fullName || [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email || "Member").trim();
}

function memberKey(member = {}) {
  return String(member.memberId || member.email || memberName(member)).toLowerCase();
}

function chapterName(chapter = {}) {
  return String(chapter.name || [chapter.city, chapter.region].filter(Boolean).join(", ") || "Chapter").trim();
}

function memberJoinDate(member = {}) {
  return parseDate(member.joinedAt || member.memberSince || member.membership?.currentTermStart || member.createdAt || member.startDate);
}

function programTitle(title = "") {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return "Class";
  }
  if (!INTERNAL_DEMO_EVENT_COPY.test(cleanTitle)) {
    return cleanTitle;
  }
  return cleanTitle
    .replace(/^Demo\s+/i, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?$/i, "")
    .trim() || "Chapter class";
}

function numberLabel(value, singular, plural = `${singular}s`) {
  const count = Number(value || 0);
  return `${count} ${count === 1 ? singular : plural}`;
}

function statusLabel(value = "") {
  const raw = String(value || "").trim();
  if (!raw) {
    return "Not recorded";
  }
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resourceName(record = {}) {
  return String(record.resourceSnapshot?.name || record.resource?.name || record.resourceName || record.name || record.title || "Range area").trim();
}

function memberEffectiveStatus(member = {}) {
  return String(member.expiration?.effectiveStatus || member.status || "unknown").toLowerCase();
}

function memberTermEnd(member = {}) {
  return parseDate(member.expiration?.currentTermEnd || member.membership?.currentTermEnd);
}

function memberExpiringSoon(member = {}) {
  const end = memberTermEnd(member);
  if (!end || memberEffectiveStatus(member) !== "active") {
    return false;
  }
  const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
  return days >= 0 && days <= 30;
}

function reservationDate(reservation = {}) {
  return parseDate(reservation.startsAt || reservation.createdAt);
}

function reservationCompleted(reservation = {}) {
  const status = String(reservation.status || "").toLowerCase();
  const attendanceStatus = String(reservation.attendance?.status || "").toLowerCase();
  return status === "completed" || attendanceStatus === "checked_out";
}

function reservationActive(reservation = {}) {
  return !["cancelled", "completed", "no_show"].includes(String(reservation.status || "").toLowerCase());
}

function reservationMemberKey(reservation = {}) {
  return String(reservation.memberId || reservation.contactEmail || reservation.memberSnapshot?.email || reservation.customerName || "").toLowerCase();
}

function reservationResourceKey(reservation = {}) {
  return String(reservation.resourceId || reservation.resourceSnapshot?.resourceId || "").toLowerCase();
}

function resourceKey(resource = {}) {
  return String(resource.resourceId || resource.id || resource.name || "").toLowerCase();
}

function blockDate(block = {}) {
  return parseDate(block.startsAt || block.startDate || block.createdAt);
}

function blockResourceName(block = {}, resources = []) {
  const resourceId = String(block.resourceId || block.resourceSnapshot?.resourceId || "").toLowerCase();
  const resource = resources.find((item) => resourceKey(item) === resourceId);
  return resourceName(block) || resourceName(resource);
}

function reservationStatusLabel(reservation = {}) {
  const status = String(reservation.status || "").toLowerCase();
  if (reservationCompleted(reservation)) {
    return "Completed visit";
  }
  if (status === "no_show") {
    return "No-show";
  }
  if (status === "cancelled") {
    return "Cancelled";
  }
  if (reservationActive(reservation)) {
    return "Reserved";
  }
  return statusLabel(status);
}

function memberLastReservation(reservations = []) {
  return reservations
    .filter((reservation) => reservationDate(reservation))
    .sort((left, right) => reservationDate(right) - reservationDate(left))[0];
}

function memberJourneyFromDetail(detail = {}) {
  const member = detail.member || {};
  const reservations = Array.isArray(detail.reservations) ? detail.reservations : [];
  const eventsById = new Map((Array.isArray(detail.events) ? detail.events : [])
    .map((event) => [String(event.eventId || ""), event]));
  const attendance = (Array.isArray(detail.attendance) ? detail.attendance : [])
    .map((record) => ({
      attendance: record,
      event: eventsById.get(String(record.eventId || "")) || {
        eventId: record.eventId,
        title: record.eventTitle || "Class attendance",
        startsAt: record.checkedInAt || record.createdAt
      }
    }))
    .sort((left, right) => String(right.event.startsAt || "").localeCompare(String(left.event.startsAt || "")));
  const sortedReservations = [...reservations]
    .sort((left, right) => (reservationDate(right)?.getTime() || 0) - (reservationDate(left)?.getTime() || 0));
  const lastReservation = memberLastReservation(sortedReservations);
  const completedVisits = sortedReservations.filter(reservationCompleted).length;
  const activeReservations = sortedReservations.filter((reservation) => reservationActive(reservation) && parseDate(reservation.startsAt) > new Date()).length;
  const lastVisitDate = sortedReservations.find(reservationCompleted);
  const lastVisitAt = reservationDate(lastVisitDate);
  return {
    member,
    joinedAt: memberJoinDate(member),
    status: memberEffectiveStatus(member),
    expiringSoon: memberExpiringSoon(member),
    reservations: sortedReservations,
    attendance,
    completedVisits,
    activeReservations,
    lastReservation,
    lastVisitAt,
    daysSinceVisit: lastVisitAt ? Math.floor((Date.now() - lastVisitAt.getTime()) / 86400000) : null
  };
}

function eventParticipantCount(event = {}, eventSummaries = {}) {
  const summary = eventSummaries[event.eventId] || {};
  if (summary.attendanceCount > 0) {
    return summary.attendanceCount;
  }
  if (summary.registrationCount > 0) {
    return summary.registrationCount;
  }
  return Number(
    event.attendanceSummary?.attended ||
    event.attendanceSummary?.total ||
    event.registrationSummary?.confirmed ||
    event.registrationSummary?.total ||
    event.registrationsCount ||
    event.attendeeCount ||
    0
  );
}

function buildBuckets(buckets, items, getDate, getValue = () => 1) {
  const granularity = buckets[0]?.granularity || "month";
  const counts = new Map(buckets.map((bucket) => [bucket.key, 0]));
  items.forEach((item) => {
    const key = bucketKey(getDate(item), granularity);
    if (counts.has(key)) {
      counts.set(key, counts.get(key) + getValue(item));
    }
  });
  return buckets.map((bucket) => ({ ...bucket, value: counts.get(bucket.key) || 0 }));
}

function buildUniqueBuckets(buckets, items, getDate, getKey) {
  const granularity = buckets[0]?.granularity || "month";
  const counts = new Map(buckets.map((bucket) => [bucket.key, new Set()]));
  items.forEach((item) => {
    const key = bucketKey(getDate(item), granularity);
    const unique = getKey(item);
    if (unique && counts.has(key)) {
      counts.get(key).add(unique);
    }
  });
  return buckets.map((bucket) => ({ ...bucket, value: counts.get(bucket.key)?.size || 0 }));
}

function rollupScopeMatches(rollup = {}, selectedChapterIds = []) {
  if (selectedChapterIds.length === 0) {
    return rollup.scopeType === "range";
  }
  return rollup.scopeType === "chapter" && selectedChapterIds.includes(rollup.scopeId);
}

function buildRollupBuckets(buckets, rollups = [], metrics = [], selectedChapterIds = []) {
  const granularity = buckets[0]?.granularity || "month";
  const metricSet = new Set(Array.isArray(metrics) ? metrics : [metrics]);
  const counts = new Map(buckets.map((bucket) => [bucket.key, 0]));
  rollups.forEach((rollup) => {
    if (!metricSet.has(rollup.metric) || rollup.granularity !== granularity || !rollupScopeMatches(rollup, selectedChapterIds)) {
      return;
    }
    const key = bucketKey(rollup.bucketStart, granularity);
    if (counts.has(key)) {
      counts.set(key, counts.get(key) + Number(rollup.value || rollup.uniqueCount || 0));
    }
  });
  return buckets.map((bucket) => ({ ...bucket, value: counts.get(bucket.key) || 0 }));
}

function Histogram({ title, buckets, tone = "members", valueLabel }) {
  const max = Math.max(1, ...buckets.map((bucket) => bucket.value));
  const granularity = buckets[0]?.granularity || "month";
  const labelStep = buckets.length > 16 ? Math.ceil(buckets.length / 12) : 1;
  const labelWidth = granularity === "day" ? 78 : granularity === "month" ? 70 : granularity === "quarter" ? 68 : 46;
  const minPlotWidth = Math.max(0, (buckets.length * labelWidth) + ((buckets.length - 1) * 6));
  const shouldExtend = minPlotWidth > 980;
  const barGap = shouldExtend ? "6px" : buckets.length > 90 ? "2px" : buckets.length > 36 ? "4px" : "10px";
  return (
    <section className={`org-insights-chart-card is-${tone}`}>
      <div className="org-insights-chart-title">
        <span>{title}</span>
        <strong>{buckets.reduce((sum, bucket) => sum + bucket.value, 0)}</strong>
      </div>
      <div className={`org-insights-histogram-frame${shouldExtend ? " is-scrollable" : ""}`}>
        <div
          className="org-insights-histogram"
          style={{
            "--insights-bar-gap": barGap,
            "--insights-bucket-count": buckets.length,
            "--insights-plot-min-width": shouldExtend ? `${minPlotWidth}px` : "100%"
          }}
        >
          {buckets.map((bucket, index) => {
            const showLabel = shouldExtend || index === 0 || index === buckets.length - 1 || index % labelStep === 0;
            const showValue = shouldExtend ? bucket.value > 0 : bucket.value > 1 || index % labelStep === 0;
            const barHeight = bucket.value ? Math.max(7, Math.round((bucket.value / max) * 100)) : 0;
            return (
            <div className="org-insights-bar-column" key={bucket.key}>
              <div className="org-insights-bar-value">{showValue ? bucket.value || "" : ""}</div>
              <div className="org-insights-bar-track">
                <span style={{ height: `${barHeight}%` }} />
              </div>
              <div className="org-insights-bar-label">{showLabel ? bucket.label : ""}</div>
            </div>
            );
          })}
        </div>
      </div>
      <span className="org-insights-chart-caption">{valueLabel}</span>
    </section>
  );
}

export function InsightsPage({ selectedRange }) {
  const chapterOrganizationMode = isChapterOrganizationRange(selectedRange || {});
  const tabs = chapterOrganizationMode ? ORGANIZATION_INSIGHT_TABS : RANGE_INSIGHT_TABS;
  const defaultTab = chapterOrganizationMode ? "trends" : "retention";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [periodDays, setPeriodDays] = useState(180);
  const [aggregateWindow, setAggregateWindow] = useState("auto");
  const [selectedMemberKey, setSelectedMemberKey] = useState("");
  const [selectedMemberJourneyDetail, setSelectedMemberJourneyDetail] = useState(null);
  const [selectedMemberJourneyLoading, setSelectedMemberJourneyLoading] = useState(false);
  const [selectedMemberJourneyError, setSelectedMemberJourneyError] = useState("");
  const [insightsMemberResults, setInsightsMemberResults] = useState([]);
  const [insightsMemberSearchLoading, setInsightsMemberSearchLoading] = useState(false);
  const [insightsMemberSearchError, setInsightsMemberSearchError] = useState("");
  const [insightsMemberSearchSummary, setInsightsMemberSearchSummary] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [customStart, setCustomStart] = useState(toDateInputValue(daysAgo(180)));
  const [customEnd, setCustomEnd] = useState(toDateInputValue(new Date()));
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventSummaries, setEventSummaries] = useState({});
  const [insightRollups, setInsightRollups] = useState([]);
  const [resources, setResources] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [resourceBlocks, setResourceBlocks] = useState([]);
  const [insightsMeta, setInsightsMeta] = useState({ detailMode: "full", notices: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const rangeId = selectedRange?.rangeId || "";

  const dateWindow = useMemo(() => {
    if (periodDays > 0) {
      return {
        start: daysAgo(periodDays),
        end: endOfDay(new Date())
      };
    }
    return {
      start: startOfDay(customStart) || daysAgo(180),
      end: endOfDay(customEnd) || endOfDay(new Date())
    };
  }, [customEnd, customStart, periodDays]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(defaultTab);
      setSelectedMemberKey("");
    }
  }, [activeTab, defaultTab, tabs]);

  useEffect(() => {
    if (!rangeId) {
      setChapters([]);
      setMembers([]);
      setEvents([]);
      setEventSummaries({});
      setInsightRollups([]);
      setResources([]);
      setReservations([]);
      setResourceBlocks([]);
      setInsightsMeta({ detailMode: "full", notices: [], summary: null });
      return undefined;
    }

    let cancelled = false;
    async function loadInsights() {
      setLoading(true);
      setError("");
      try {
        const summary = await getInsightsSummary(rangeId, {
          startsAt: dateWindow.start?.toISOString(),
          endsAt: dateWindow.end?.toISOString()
        });
        if (!cancelled && summary?.source === "backend_summary") {
          setInsightsMeta({
            detailMode: summary.detailMode || "full",
            limits: summary.limits || {},
            notices: summary.notices || [],
            rollupWindow: summary.rollupWindow || null,
            summary: summary.summary || null
          });
          setChapters(summary.chapters || []);
          setMembers(summary.members || []);
          setEvents(summary.events || []);
          setResources(summary.resources || []);
          setReservations(summary.reservations || []);
          setResourceBlocks(summary.resourceBlocks || summary.blocks || []);
          setEventSummaries(summary.eventSummaries || {});
          setInsightRollups(summary.rollups || []);
          setLoading(false);
          return;
        }
      } catch {
        // Older deployments may not have the summary endpoint yet. Keep the
        // existing list-based path as a rollout fallback.
      }

      try {
        const [chapterResult, memberResult, eventResult, resourceResult, reservationResult, blockResult] = await Promise.all([
          chapterOrganizationMode ? listChapters(rangeId) : Promise.resolve({ chapters: [] }),
          listMembers(rangeId),
          listEvents(rangeId),
          chapterOrganizationMode ? Promise.resolve({ resources: [] }) : getResourceSummary(rangeId),
          chapterOrganizationMode ? Promise.resolve({ reservations: [] }) : listResourceReservations(rangeId),
          chapterOrganizationMode ? Promise.resolve({ blocks: [] }) : listResourceBlocks(rangeId)
        ]);
        const nextEvents = eventResult?.events || [];
        if (!cancelled) {
          setChapters(chapterResult?.chapters || []);
          setMembers(Array.isArray(memberResult) ? memberResult : memberResult?.members || []);
          setEvents(nextEvents);
          setResources(resourceResult?.resources || resourceResult?.items || []);
          setReservations(reservationResult?.reservations || reservationResult?.items || []);
          setResourceBlocks(blockResult?.blocks || blockResult?.items || []);
          setEventSummaries({});
          setInsightRollups([]);
          setInsightsMeta({ detailMode: "full", notices: [], summary: null });
          setLoading(false);
        }

        for (const event of nextEvents) {
          if (cancelled || !event.eventId) {
            continue;
          }
          try {
            const [registrationsResult, attendanceResult] = await Promise.all([
              listEventRegistrations(rangeId, event.eventId),
              listEventAttendance(rangeId, event.eventId)
            ]);
            const registrations = registrationsResult?.registrations || registrationsResult || [];
            const attendance = attendanceResult?.attendance || attendanceResult?.records || attendanceResult || [];
            if (!cancelled) {
              setEventSummaries((current) => ({
                ...current,
                [event.eventId]: {
                  registrationCount: registrations.filter((item) => item.status !== "cancelled").length,
                  attendanceCount: attendance.filter((item) => item.status === "attended").length,
                  registrations,
                  attendance
                }
              }));
            }
          } catch {
            if (!cancelled) {
              setEventSummaries((current) => ({
                ...current,
                [event.eventId]: {
                  registrationCount: eventParticipantCount(event),
                  attendanceCount: 0,
                  registrations: [],
                  attendance: []
                }
              }));
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Insights could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadInsights();
    return () => {
      cancelled = true;
    };
  }, [chapterOrganizationMode, dateWindow.end, dateWindow.start, rangeId]);

  useEffect(() => {
    const available = new Set(chapters.map((chapter) => chapter.chapterId).filter(Boolean));
    setSelectedChapterIds((current) => current.filter((chapterId) => available.has(chapterId)));
  }, [chapters]);

  const insights = useMemo(() => {
    const selectedMembers = members.filter((member) => memberMatchesChapter(member, selectedChapterIds));
    const selectedEvents = events
      .filter((event) => eventMatchesChapter(event, selectedChapterIds))
      .sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")));
    const inWindowMembers = selectedMembers.filter((member) => {
      const joinedAt = memberJoinDate(member);
      return joinedAt && joinedAt >= dateWindow.start && joinedAt <= dateWindow.end;
    });
    const inWindowEvents = selectedEvents.filter((event) => {
      const startsAt = parseDate(event.startsAt);
      return startsAt && startsAt >= dateWindow.start && startsAt <= dateWindow.end;
    });
    const timeBuckets = getTimeBuckets(dateWindow.start, dateWindow.end, aggregateWindow);
    const memberBuckets = buildBuckets(timeBuckets, inWindowMembers, memberJoinDate);
    const classBuckets = buildBuckets(timeBuckets, inWindowEvents, (event) => event.startsAt);
    const participantBuckets = buildBuckets(
      timeBuckets,
      inWindowEvents,
      (event) => event.startsAt,
      (event) => eventParticipantCount(event, eventSummaries)
    );
    const memberByEmail = new Map(selectedMembers.map((member) => [String(member.email || "").toLowerCase(), member]));
    const attendanceByEmail = new Map();
    Object.entries(eventSummaries).forEach(([eventId, summary]) => {
      const event = selectedEvents.find((item) => item.eventId === eventId);
      if (!event) {
        return;
      }
      (summary.attendance || []).forEach((attendance) => {
        if (attendance.status && attendance.status !== "attended") {
          return;
        }
        const email = String(attendance.email || attendance.memberSnapshot?.email || "").toLowerCase();
        if (!email || !memberByEmail.has(email)) {
          return;
        }
        const existing = attendanceByEmail.get(email) || [];
        existing.push({ event, attendance });
        attendanceByEmail.set(email, existing);
      });
    });
    const chapterTimeline = [
      ...inWindowMembers.map((member) => ({
        type: "member",
        date: memberJoinDate(member),
        title: memberName(member),
        detail: "New member",
        chapter: chapters.find((chapter) => memberChapterIds(member).includes(chapter.chapterId))
      })),
      ...inWindowEvents.map((event) => ({
        type: "class",
        date: parseDate(event.startsAt),
        title: programTitle(event.title),
        detail: numberLabel(eventParticipantCount(event, eventSummaries), "participant"),
        chapter: chapters.find((chapter) => eventChapterIds(event).includes(chapter.chapterId)),
        event
      }))
    ].filter((item) => item.date).sort((left, right) => right.date - left.date);
    const memberJourney = selectedMembers
      .map((member) => {
        const email = String(member.email || "").toLowerCase();
        const attendance = (attendanceByEmail.get(email) || []).sort((left, right) => (
          String(left.event.startsAt || "").localeCompare(String(right.event.startsAt || ""))
        ));
        const chapter = chapters.find((item) => memberChapterIds(member).includes(item.chapterId));
        return {
          member,
          chapter,
          joinedAt: memberJoinDate(member),
          attendance,
          firstClass: attendance[0]?.event,
          lastClass: attendance[attendance.length - 1]?.event
        };
      })
      .sort((left, right) => {
        const leftTime = left.joinedAt?.getTime() || 0;
        const rightTime = right.joinedAt?.getTime() || 0;
        return rightTime - leftTime;
      });

    return {
      classBuckets,
      chapterTimeline,
      inWindowEvents,
      inWindowMembers,
      memberBuckets,
      memberJourney,
      participantBuckets,
      selectedEvents,
      selectedMembers
    };
  }, [aggregateWindow, chapters, dateWindow, eventSummaries, events, members, selectedChapterIds]);

  const limitedInsights = insightsMeta.detailMode === "limited";

  const rangeInsights = useMemo(() => {
    const timeBuckets = getTimeBuckets(dateWindow.start, dateWindow.end, aggregateWindow);
    const inWindowMembers = members.filter((member) => {
      const joinedAt = memberJoinDate(member);
      return joinedAt && joinedAt >= dateWindow.start && joinedAt <= dateWindow.end;
    });
    const inWindowReservations = reservations.filter((reservation) => {
      const startsAt = reservationDate(reservation);
      return startsAt && startsAt >= dateWindow.start && startsAt <= dateWindow.end;
    });
    const inWindowEvents = events.filter((event) => {
      const startsAt = parseDate(event.startsAt);
      return startsAt && startsAt >= dateWindow.start && startsAt <= dateWindow.end;
    });
    const inWindowBlocks = resourceBlocks.filter((block) => {
      const startsAt = blockDate(block);
      return startsAt && startsAt >= dateWindow.start && startsAt <= dateWindow.end;
    });
    const completedReservations = inWindowReservations.filter(reservationCompleted);
    const cancelledReservations = inWindowReservations.filter((reservation) => (
      ["cancelled", "no_show"].includes(String(reservation.status || "").toLowerCase())
    ));
    const resourceBreakdownMap = new Map();
    inWindowReservations.forEach((reservation) => {
      const key = reservationResourceKey(reservation) || resourceName(reservation).toLowerCase();
      const current = resourceBreakdownMap.get(key) || {
        resource: { name: resourceName(reservation), resourceId: key },
        total: 0,
        completed: 0,
        cancelled: 0,
        noShow: 0
      };
      current.total += 1;
      if (reservationCompleted(reservation)) {
        current.completed += 1;
      }
      if (String(reservation.status || "").toLowerCase() === "cancelled") {
        current.cancelled += 1;
      }
      if (String(reservation.status || "").toLowerCase() === "no_show") {
        current.noShow += 1;
      }
      resourceBreakdownMap.set(key, current);
    });
    const resourceBreakdown = Array.from(resourceBreakdownMap.values())
      .sort((left, right) => right.total - left.total)
      .slice(0, 10);
    const maxResourceTotal = Math.max(1, ...resourceBreakdown.map((item) => item.total));
    const rangeTimeline = [
      ...inWindowMembers.map((member) => ({
        type: "member",
        date: memberJoinDate(member),
        title: memberName(member),
        detail: `${statusLabel(memberEffectiveStatus(member))} member`,
        actionLabel: "Member",
        member
      })),
      ...inWindowReservations.map((reservation) => ({
        type: "reservation",
        date: reservationDate(reservation),
        title: resourceName(reservation),
        detail: `${reservationStatusLabel(reservation)} · ${reservation.customerName || reservation.memberSnapshot?.fullName || "Member"}`,
        actionLabel: reservationStatusLabel(reservation),
        reservation
      })),
      ...inWindowEvents.map((event) => ({
        type: "class",
        date: parseDate(event.startsAt),
        title: programTitle(event.title),
        detail: numberLabel(eventParticipantCount(event, eventSummaries), "participant"),
        actionLabel: "Class",
        event
      })),
      ...inWindowBlocks.map((block) => ({
        type: "block",
        date: blockDate(block),
        title: blockResourceName(block, resources),
        detail: statusLabel(block.reason || block.status || "Maintenance"),
        actionLabel: "Closure",
        block
      }))
    ].filter((item) => item.date).sort((left, right) => right.date - left.date);

    const reservationsByMember = new Map();
    reservations.forEach((reservation) => {
      const keys = [
        String(reservation.memberId || "").toLowerCase(),
        String(reservation.contactEmail || "").toLowerCase(),
        String(reservation.memberSnapshot?.email || "").toLowerCase()
      ].filter(Boolean);
      keys.forEach((key) => {
        const existing = reservationsByMember.get(key) || [];
        existing.push(reservation);
        reservationsByMember.set(key, existing);
      });
    });
    const attendanceByMember = new Map();
    Object.entries(eventSummaries).forEach(([eventId, summary]) => {
      const event = events.find((item) => item.eventId === eventId);
      if (!event) {
        return;
      }
      (summary.attendance || []).forEach((attendance) => {
        const keys = [
          String(attendance.memberId || "").toLowerCase(),
          String(attendance.email || "").toLowerCase(),
          String(attendance.memberSnapshot?.email || "").toLowerCase()
        ].filter(Boolean);
        keys.forEach((key) => {
          const existing = attendanceByMember.get(key) || [];
          existing.push({ event, attendance });
          attendanceByMember.set(key, existing);
        });
      });
    });
    const query = memberSearch.trim().toLowerCase();
    const memberJourney = members
      .map((member) => {
        const id = String(member.memberId || "").toLowerCase();
        const email = String(member.email || "").toLowerCase();
        const memberReservations = [
          ...(reservationsByMember.get(id) || []),
          ...(reservationsByMember.get(email) || [])
        ].filter((reservation, index, list) => list.findIndex((item) => item.reservationId === reservation.reservationId) === index)
          .sort((left, right) => (reservationDate(right)?.getTime() || 0) - (reservationDate(left)?.getTime() || 0));
        const memberAttendance = [
          ...(attendanceByMember.get(id) || []),
          ...(attendanceByMember.get(email) || [])
        ].filter((item, index, list) => list.findIndex((candidate) => (
          candidate.event.eventId === item.event.eventId &&
          (candidate.attendance.attendanceId || candidate.attendance.email) === (item.attendance.attendanceId || item.attendance.email)
        )) === index)
          .sort((left, right) => String(right.event.startsAt || "").localeCompare(String(left.event.startsAt || "")));
        const lastReservation = memberLastReservation(memberReservations);
        const completedVisits = memberReservations.filter(reservationCompleted).length;
        const activeReservations = memberReservations.filter((reservation) => reservationActive(reservation) && parseDate(reservation.startsAt) > new Date()).length;
        const lastVisitDate = memberReservations.find(reservationCompleted);
        const lastVisitAt = reservationDate(lastVisitDate);
        const daysSinceVisit = lastVisitAt ? Math.floor((Date.now() - lastVisitAt.getTime()) / 86400000) : null;
        return {
          member,
          joinedAt: memberJoinDate(member),
          status: memberEffectiveStatus(member),
          expiringSoon: memberExpiringSoon(member),
          reservations: memberReservations,
          attendance: memberAttendance,
          completedVisits,
          activeReservations,
          lastReservation,
          lastVisitAt,
          daysSinceVisit
        };
      })
      .filter((item) => {
        if (!query) {
          return true;
        }
        return [
          memberName(item.member),
          item.member.email,
          item.member.phone,
          item.member.memberNumber,
          item.status
        ].some((value) => String(value || "").toLowerCase().includes(query));
      })
      .sort((left, right) => {
        const leftVisit = left.lastVisitAt?.getTime() || 0;
        const rightVisit = right.lastVisitAt?.getTime() || 0;
        if (rightVisit !== leftVisit) {
          return rightVisit - leftVisit;
        }
        return (right.joinedAt?.getTime() || 0) - (left.joinedAt?.getTime() || 0);
      });
    const activeMembers = members.filter((member) => memberEffectiveStatus(member) === "active");
    const inactiveMembers = members.filter((member) => memberEffectiveStatus(member) === "inactive");
    const pendingMembers = members.filter((member) => memberEffectiveStatus(member) === "pending");
    const expiringMembers = members.filter(memberExpiringSoon);
    const atRiskMembers = memberJourney.filter((item) => (
      item.status === "active" &&
      (!item.lastVisitAt || Number(item.daysSinceVisit) >= 60)
    ));
    const repeatVisitors = memberJourney.filter((item) => item.completedVisits >= 3);
    const useRollupBuckets = limitedInsights && insightRollups.length > 0;
    const memberBuckets = useRollupBuckets
      ? buildRollupBuckets(timeBuckets, insightRollups, "memberJoinCount", selectedChapterIds)
      : buildBuckets(timeBuckets, inWindowMembers, memberJoinDate);
    const reservationBuckets = useRollupBuckets
      ? buildRollupBuckets(timeBuckets, insightRollups, "reservationCreatedCount", selectedChapterIds)
      : buildBuckets(timeBuckets, inWindowReservations, reservationDate);
    const returningReservationBuckets = useRollupBuckets
      ? buildRollupBuckets(timeBuckets, insightRollups, "reservationUniqueMemberCount", selectedChapterIds)
      : buildUniqueBuckets(
        timeBuckets,
        completedReservations,
        reservationDate,
        reservationMemberKey
      );
    const classBuckets = useRollupBuckets
      ? buildRollupBuckets(timeBuckets, insightRollups, "classCount", selectedChapterIds)
      : buildBuckets(timeBuckets, inWindowEvents, (event) => event.startsAt);
    const statusBuckets = useRollupBuckets
      ? buildRollupBuckets(timeBuckets, insightRollups, ["reservationCancelledCount", "reservationNoShowCount"], selectedChapterIds)
      : buildBuckets(timeBuckets, cancelledReservations, reservationDate);

    return {
      activeMembers,
      atRiskMembers,
      cancelledReservations,
      classBuckets,
      completedReservations,
      expiringMembers,
      inactiveMembers,
      inWindowEvents,
      inWindowMembers,
      inWindowReservations,
      memberBuckets,
      memberJourney,
      pendingMembers,
      rangeTimeline,
      repeatVisitors,
      reservationBuckets,
      returningReservationBuckets,
      resourceBreakdown,
      maxResourceTotal,
      resourceBlocks: inWindowBlocks,
      renewalBuckets: buildBuckets(timeBuckets, members.filter(memberTermEnd), memberTermEnd),
      statusBuckets,
      usesRollups: useRollupBuckets
    };
  }, [aggregateWindow, dateWindow, eventSummaries, events, insightRollups, limitedInsights, memberSearch, members, reservations, resourceBlocks, resources, selectedChapterIds]);

  function toggleChapter(chapterId) {
    setSelectedChapterIds((current) => (
      current.includes(chapterId)
        ? current.filter((item) => item !== chapterId)
        : [...current, chapterId]
    ));
  }

  function resetChapters() {
    setSelectedChapterIds([]);
  }

  const limitedSummary = insightsMeta.summary || {};
  const insightsNotice = Array.isArray(insightsMeta.notices) ? insightsMeta.notices[0] : null;
  const selectedLabel = limitedInsights
    ? `${numberLabel(limitedSummary.members || 0, "member")} · ${numberLabel(limitedSummary.resources || 0, "resource")}`
    : chapterOrganizationMode
    ? selectedChapterIds.length === 0
      ? "All chapters"
      : chapters
        .filter((chapter) => selectedChapterIds.includes(chapter.chapterId))
        .map(chapterName)
        .join(", ")
    : `${numberLabel(members.length, "member")} · ${numberLabel(resources.length, "resource")}`;
  const showWindowFilter = activeTab !== "member-journey";
  const showAggregateFilter = chapterOrganizationMode
    ? activeTab === "trends"
    : activeTab === "retention" || activeTab === "usage";
  const useLimitedMemberSearch = !chapterOrganizationMode && limitedInsights && activeTab === "member-journey";
  const rangeMemberJourneyList = useMemo(() => (
    useLimitedMemberSearch
      ? insightsMemberResults.map((member) => memberJourneyFromDetail({ member }))
      : rangeInsights.memberJourney
  ), [insightsMemberResults, rangeInsights.memberJourney, useLimitedMemberSearch]);
  const localSelectedMemberJourney = chapterOrganizationMode
    ? insights.memberJourney.find((item) => memberKey(item.member) === selectedMemberKey)
    : rangeMemberJourneyList.find((item) => memberKey(item.member) === selectedMemberKey);
  const selectedMemberJourney = !chapterOrganizationMode && selectedMemberJourneyDetail && memberKey(selectedMemberJourneyDetail.member) === selectedMemberKey
    ? selectedMemberJourneyDetail
    : localSelectedMemberJourney;

  useEffect(() => {
    if (!useLimitedMemberSearch || !rangeId || selectedMemberKey) {
      if (!useLimitedMemberSearch) {
        setInsightsMemberResults([]);
        setInsightsMemberSearchSummary(null);
        setInsightsMemberSearchError("");
        setInsightsMemberSearchLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    setInsightsMemberSearchLoading(true);
    setInsightsMemberSearchError("");
    const timeout = setTimeout(() => {
      listInsightsMembers(rangeId, {
        q: memberSearch.trim(),
        limit: 50
      })
        .then((result) => {
          if (!cancelled) {
            setInsightsMemberResults(result?.members || []);
            setInsightsMemberSearchSummary(result?.summary || null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setInsightsMemberResults([]);
            setInsightsMemberSearchSummary(null);
            setInsightsMemberSearchError(err.message || "Members could not be loaded.");
          }
        })
        .finally(() => {
          if (!cancelled) {
            setInsightsMemberSearchLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [memberSearch, rangeId, selectedMemberKey, useLimitedMemberSearch]);

  useEffect(() => {
    if (chapterOrganizationMode || !rangeId || !selectedMemberKey) {
      setSelectedMemberJourneyDetail(null);
      setSelectedMemberJourneyLoading(false);
      setSelectedMemberJourneyError("");
      return undefined;
    }
    const memberId = localSelectedMemberJourney?.member?.memberId;
    if (!memberId) {
      setSelectedMemberJourneyDetail(null);
      setSelectedMemberJourneyLoading(false);
      setSelectedMemberJourneyError("");
      return undefined;
    }

    let cancelled = false;
    setSelectedMemberJourneyLoading(true);
    setSelectedMemberJourneyError("");
    setSelectedMemberJourneyDetail(null);
    getInsightsMemberJourney(rangeId, memberId, {
      startsAt: dateWindow.start?.toISOString(),
      endsAt: dateWindow.end?.toISOString()
    })
      .then((detail) => {
        if (!cancelled) {
          setSelectedMemberJourneyDetail(memberJourneyFromDetail(detail));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSelectedMemberJourneyDetail(null);
          setSelectedMemberJourneyError(err.message || "Member journey could not be refreshed.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSelectedMemberJourneyLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chapterOrganizationMode, dateWindow.end, dateWindow.start, localSelectedMemberJourney, rangeId, selectedMemberKey]);

  const memberTotalAttendance = insights.memberJourney.reduce((sum, item) => sum + item.attendance.length, 0);
  const memberEngagedCount = insights.memberJourney.filter((item) => item.attendance.length > 0).length;
  const metricCards = limitedInsights
    ? [
      ["Members", limitedSummary.members || 0],
      ["Reservations", limitedSummary.reservations || 0],
      ["Classes", limitedSummary.events || 0],
      ["Resources", limitedSummary.resources || 0]
    ]
    : chapterOrganizationMode
    ? activeTab === "member-journey"
      ? [
        ["Members", insights.selectedMembers.length],
        ["Class visits", memberTotalAttendance],
        ["With attendance", memberEngagedCount],
        ["Chapters", selectedChapterIds.length || chapters.length]
      ]
      : [
        ["Members", insights.selectedMembers.length],
        ["New members", insights.inWindowMembers.length],
        ["Classes", insights.inWindowEvents.length],
        ["Participants", insights.participantBuckets.reduce((sum, bucket) => sum + bucket.value, 0)]
      ]
    : activeTab === "usage"
      ? [
        ["Reservations", rangeInsights.inWindowReservations.length],
        ["Completed visits", rangeInsights.completedReservations.length],
        ["Cancellations", rangeInsights.cancelledReservations.length],
        ["Resources used", rangeInsights.resourceBreakdown.length]
      ]
      : activeTab === "range-journey"
        ? [
          ["Activity", rangeInsights.rangeTimeline.length],
          ["Reservations", rangeInsights.inWindowReservations.length],
          ["Classes", rangeInsights.inWindowEvents.length],
          ["Closures", rangeInsights.resourceBlocks.length]
        ]
        : activeTab === "member-journey"
          ? [
            ["Members", rangeInsights.memberJourney.length],
            ["Repeat visitors", rangeInsights.repeatVisitors.length],
            ["At risk", rangeInsights.atRiskMembers.length],
            ["Expiring soon", rangeInsights.expiringMembers.length]
          ]
          : [
            ["Members", members.length],
            ["New members", rangeInsights.inWindowMembers.length],
            ["Active", rangeInsights.activeMembers.length],
            ["Expiring soon", rangeInsights.expiringMembers.length]
          ];
  const headerKicker = chapterOrganizationMode ? "Organization insights" : "Range insights";
  const headerTitle = chapterOrganizationMode ? "Chapter growth over time" : "Retention and range usage";

  if (!selectedRange) {
    return (
      <section className="panel page-panel">
        <div className="empty-panel">
          <strong>Insights unavailable</strong>
          <span>Open an organization workspace first.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="panel module-panel org-insights-workspace" id="insights">
      {loading && <div className="status-strip">Refreshing insights...</div>}
      {error && <div className="status-strip error-strip">{error}</div>}
      {insightsNotice && (
        <div className="status-strip insights-scale-notice">
          <strong>{insightsNotice.title || "Choose a shorter date range"}</strong>
          <span>{insightsNotice.detail || "This workspace has more history than the current detail view can show at once."} Use a shorter window to see individual activity and member journeys.</span>
        </div>
      )}

      <div className="membership-section-overview org-insights-overview">
        <div>
          <span className="section-kicker">{headerKicker}</span>
          <h3>{headerTitle}</h3>
        </div>
        <div className="membership-context-metrics org-insights-metrics">
          {metricCards.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="org-insights-toolbar">
        <div className="org-insights-tabs" role="tablist" aria-label="Insights sections">
          {tabs.map((tab) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`insights-panel-${tab.id}`}
              className={activeTab === tab.id ? "is-active" : ""}
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedMemberKey("");
              }}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        <div className="org-insights-filter-summary">{selectedLabel}</div>
      </div>

      <div className={`org-insights-filters is-${activeTab}`}>
        {showWindowFilter && (
          <label>
            Window
            <select value={periodDays} onChange={(event) => setPeriodDays(Number(event.target.value))}>
              {PRESET_WINDOWS.map((window) => (
                <option value={window.days} key={window.label}>{window.label}</option>
              ))}
            </select>
          </label>
        )}
        {showAggregateFilter && (
          <label>
            Group bars
            <select value={aggregateWindow} onChange={(event) => setAggregateWindow(event.target.value)}>
              {AGGREGATE_WINDOWS.map((window) => (
                <option value={window.value} key={window.value}>{window.label}</option>
              ))}
            </select>
          </label>
        )}
        {showWindowFilter && periodDays === 0 && (
          <>
            <label>
              Start
              <input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
            </label>
            <label>
              End
              <input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
            </label>
          </>
        )}
        {chapterOrganizationMode && (
          <div className="org-insights-chapter-select">
            <span className="org-insights-field-label">Chapter</span>
            <details className="org-insights-chapter-dropdown">
              <summary>{selectedLabel}</summary>
              <div className="org-insights-chapter-menu">
                <label>
                  <input
                    checked={selectedChapterIds.length === 0}
                    onChange={resetChapters}
                    type="checkbox"
                  />
                  <span>All chapters</span>
                </label>
                {chapters.map((chapter) => (
                  <label key={chapter.chapterId}>
                    <input
                      checked={selectedChapterIds.includes(chapter.chapterId)}
                      onChange={() => toggleChapter(chapter.chapterId)}
                      type="checkbox"
                    />
                    <span>{chapterName(chapter)}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>
        )}
        {!chapterOrganizationMode && activeTab === "member-journey" && !selectedMemberJourney && (
          <label className="org-insights-member-search">
            Search members
            <input
              type="search"
              value={memberSearch}
              onChange={(event) => setMemberSearch(event.target.value)}
              placeholder="Name, email, phone, status"
            />
          </label>
        )}
      </div>

      {chapterOrganizationMode && activeTab === "trends" && (
        <div className="org-insights-chart-grid" id="insights-panel-trends" role="tabpanel">
          <Histogram title="New members" buckets={insights.memberBuckets} tone="members" valueLabel="Joined during selected window" />
          <Histogram title="Classes" buckets={insights.classBuckets} tone="classes" valueLabel="Classes scheduled during selected window" />
          <Histogram title="Participants" buckets={insights.participantBuckets} tone="participants" valueLabel="Attendance when recorded, registrations otherwise" />
        </div>
      )}

      {!chapterOrganizationMode && activeTab === "retention" && (
        <div className="org-insights-chart-grid" id="insights-panel-retention" role="tabpanel">
          <Histogram title="New members" buckets={rangeInsights.memberBuckets} tone="members" valueLabel="Joined during selected window" />
          <Histogram title="Returning members" buckets={rangeInsights.returningReservationBuckets} tone="visits" valueLabel="Unique members with completed range visits" />
          <Histogram title="Renewals due" buckets={rangeInsights.renewalBuckets} tone="renewals" valueLabel="Membership terms ending in the selected window" />
        </div>
      )}

      {!chapterOrganizationMode && activeTab === "usage" && (
        <div className="org-insights-chart-grid" id="insights-panel-usage" role="tabpanel">
          <Histogram title="Reservations" buckets={rangeInsights.reservationBuckets} tone="reservations" valueLabel="Member reservations booked for the selected window" />
          <Histogram title="Completed visits" buckets={rangeInsights.returningReservationBuckets} tone="visits" valueLabel="Unique members checked out from range visits" />
          <Histogram title="Cancellations and no-shows" buckets={rangeInsights.statusBuckets} tone="risk" valueLabel="Reservations that did not turn into a visit" />
          <section className="org-insights-chart-card org-insights-resource-breakdown">
            <div className="org-insights-chart-title">
              <span>Top resource mix</span>
              <strong>{rangeInsights.resourceBreakdown.reduce((sum, item) => sum + item.total, 0)}</strong>
            </div>
            <div className="org-insights-resource-list">
              {rangeInsights.resourceBreakdown.map((item) => (
                <div className="org-insights-resource-row" key={resourceKey(item.resource)}>
                  <div>
                    <strong>{resourceName(item.resource)}</strong>
                    <span>{numberLabel(item.completed, "completed visit")} · {numberLabel(item.cancelled + item.noShow, "missed booking")}</span>
                  </div>
                  <div className="org-insights-resource-meter" aria-hidden="true">
                    <span style={{ width: `${Math.max(8, Math.round((item.total / rangeInsights.maxResourceTotal) * 100))}%` }} />
                  </div>
                  <strong>{item.total}</strong>
                </div>
              ))}
              {rangeInsights.resourceBreakdown.length === 0 && <div className="org-insights-empty">No resource activity in this window.</div>}
            </div>
          </section>
        </div>
      )}

      {chapterOrganizationMode && activeTab === "chapter-journey" && (
        <div className="org-insights-journey-list" id="insights-panel-chapter-journey" role="tabpanel">
          {insights.chapterTimeline.map((item) => (
            <article key={`${item.type}-${item.title}-${item.date.toISOString()}`} className={`is-${item.type}`}>
              <div className="org-insights-journey-date">
                <time>{formatDate(item.date)}</time>
                <span>{item.type === "class" ? "Class" : "New member"}</span>
              </div>
              <div>
                <strong>{item.title}</strong>
                <span>{item.chapter ? chapterName(item.chapter) : "Chapter not set"} · {item.detail}</span>
              </div>
              {item.event ? (
                <a href={`/console/ranges/${encodeURIComponent(rangeId)}/apps/events?eventId=${encodeURIComponent(item.event.eventId)}#events-roster`}>
                  Attendance
                </a>
              ) : null}
            </article>
          ))}
          {insights.chapterTimeline.length === 0 && <div className="org-insights-empty">No chapter movement in this window.</div>}
        </div>
      )}

      {!chapterOrganizationMode && activeTab === "range-journey" && (
        <div className="org-insights-journey-list" id="insights-panel-range-journey" role="tabpanel">
          {rangeInsights.rangeTimeline.map((item, index) => (
            <article key={`${item.type}-${item.title}-${item.date.toISOString()}-${index}`} className={`is-${item.type}`}>
              <div className="org-insights-journey-date">
                <time>{formatDate(item.date)}</time>
                <span>{item.actionLabel}</span>
              </div>
              <div>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
              </div>
              {item.event ? (
                <a href={`/console/ranges/${encodeURIComponent(rangeId)}/apps/events?eventId=${encodeURIComponent(item.event.eventId)}#events-roster`}>
                  Attendance
                </a>
              ) : item.member ? (
                <button type="button" className="org-insights-journey-kind" onClick={() => {
                  setActiveTab("member-journey");
                  setSelectedMemberKey(memberKey(item.member));
                }}>
                  Journey
                </button>
              ) : (
                <span className="org-insights-journey-kind">{item.actionLabel}</span>
              )}
            </article>
          ))}
          {rangeInsights.rangeTimeline.length === 0 && <div className="org-insights-empty">No range activity in this window.</div>}
        </div>
      )}

      {chapterOrganizationMode && activeTab === "member-journey" && (
        <div className="org-insights-member-journey" id="insights-panel-member-journey" role="tabpanel">
          {selectedMemberJourney ? (
            <section className="org-insights-member-detail">
              <div className="org-insights-member-detail-header">
                <button type="button" className="compact-button" onClick={() => setSelectedMemberKey("")}>
                  Back to members
                </button>
                <div>
                  <strong>{memberName(selectedMemberJourney.member)}</strong>
                  <span>{selectedMemberJourney.member.email}</span>
                </div>
              </div>
              <div className="org-insights-member-detail-grid">
                <div>
                  <span>Chapter</span>
                  <strong>{selectedMemberJourney.chapter ? chapterName(selectedMemberJourney.chapter) : "Chapter not set"}</strong>
                </div>
                <div>
                  <span>Joined</span>
                  <strong>{formatDate(selectedMemberJourney.joinedAt)}</strong>
                </div>
                <div>
                  <span>Classes attended</span>
                  <strong>{selectedMemberJourney.attendance.length}</strong>
                </div>
                <div>
                  <span>Last class</span>
                  <strong>{selectedMemberJourney.lastClass ? formatDate(selectedMemberJourney.lastClass.startsAt) : "No class yet"}</strong>
                </div>
              </div>
              <div className="org-insights-member-timeline">
                {[...selectedMemberJourney.attendance].reverse().map(({ event, attendance }) => (
                  <article className="is-class" key={`${event.eventId}-${attendance.email || selectedMemberJourney.member.email}`}>
                    <time>{formatDate(event.startsAt)}</time>
                    <div>
                      <strong>{programTitle(event.title)}</strong>
                      <span>{formatEventWindow(event)}</span>
                    </div>
                    <a href={`/console/ranges/${encodeURIComponent(rangeId)}/apps/events?eventId=${encodeURIComponent(event.eventId)}#events-roster`}>
                      Attendance
                    </a>
                  </article>
                ))}
                {selectedMemberJourney.attendance.length === 0 && (
                  <div className="org-insights-empty">No class attendance recorded yet.</div>
                )}
                <article className="is-joined">
                  <time>{formatDate(selectedMemberJourney.joinedAt)}</time>
                  <div>
                    <strong>Joined {selectedMemberJourney.chapter ? chapterName(selectedMemberJourney.chapter) : "the organization"}</strong>
                    <span>{selectedMemberJourney.member.email}</span>
                  </div>
                </article>
              </div>
            </section>
          ) : insights.memberJourney.map((item) => (
            <button
              type="button"
              className="org-insights-member-row"
              key={memberKey(item.member)}
              onClick={() => setSelectedMemberKey(memberKey(item.member))}
            >
              <div>
                <strong>{memberName(item.member)}</strong>
                <span>{item.member.email}</span>
              </div>
              <div>
                <span>{item.chapter ? chapterName(item.chapter) : "Chapter not set"}</span>
                <small>Joined {formatDate(item.joinedAt)}</small>
              </div>
              <div>
                <strong>{item.attendance.length}</strong>
                <small>Classes attended</small>
              </div>
              <div>
                <span>{item.lastClass ? programTitle(item.lastClass.title) : "No class yet"}</span>
                <small>{item.lastClass ? formatEventWindow(item.lastClass) : "Waiting for first attendance"}</small>
              </div>
            </button>
          ))}
          {insights.memberJourney.length === 0 && <div className="org-insights-empty">No members in this view.</div>}
        </div>
      )}

      {!chapterOrganizationMode && activeTab === "member-journey" && (
        <div className="org-insights-member-journey" id="insights-panel-member-journey" role="tabpanel">
          {selectedMemberJourney ? (
            <section className="org-insights-member-detail">
              <div className="org-insights-member-detail-header">
                <button type="button" className="compact-button" onClick={() => setSelectedMemberKey("")}>
                  Back to members
                </button>
                <div>
                  <strong>{memberName(selectedMemberJourney.member)}</strong>
                  <span>{selectedMemberJourney.member.email}</span>
                </div>
              </div>
              {selectedMemberJourneyLoading && (
                <div className="status-strip">
                  Refreshing member journey
                </div>
              )}
              {selectedMemberJourneyError && (
                <div className="alert alert-warning">
                  {selectedMemberJourneyError}
                </div>
              )}
              <div className="org-insights-member-detail-grid">
                <div>
                  <span>Status</span>
                  <strong>{statusLabel(selectedMemberJourney.status)}</strong>
                </div>
                <div>
                  <span>Joined</span>
                  <strong>{formatDate(selectedMemberJourney.joinedAt)}</strong>
                </div>
                <div>
                  <span>Completed visits</span>
                  <strong>{selectedMemberJourney.completedVisits}</strong>
                </div>
                <div>
                  <span>Last visit</span>
                  <strong>{selectedMemberJourney.lastVisitAt ? formatDate(selectedMemberJourney.lastVisitAt) : "No visit yet"}</strong>
                </div>
              </div>
              <div className="org-insights-member-timeline">
                {[
                  ...selectedMemberJourney.reservations.map((reservation) => ({
                    type: "reservation",
                    date: reservationDate(reservation),
                    title: resourceName(reservation),
                    detail: `${reservationStatusLabel(reservation)} · ${formatEventWindow(reservation)}`,
                    reservation
                  })),
                  ...selectedMemberJourney.attendance.map(({ event }) => ({
                    type: "class",
                    date: parseDate(event.startsAt),
                    title: programTitle(event.title),
                    detail: formatEventWindow(event),
                    event
                  })),
                  ...(Array.isArray(selectedMemberJourney.member.statusHistory) ? selectedMemberJourney.member.statusHistory.map((entry) => ({
                    type: "status",
                    date: parseDate(entry.createdAt),
                    title: statusLabel(entry.toStatus || entry.status),
                    detail: entry.reason || "Membership status changed"
                  })) : []),
                  {
                    type: "joined",
                    date: selectedMemberJourney.joinedAt,
                    title: "Joined the range",
                    detail: selectedMemberJourney.member.email
                  }
                ].filter((item) => item.date).sort((left, right) => right.date - left.date).map((item, index) => (
                  <article className={`is-${item.type}`} key={`${item.type}-${item.title}-${item.date.toISOString()}-${index}`}>
                    <time>{formatDate(item.date)}</time>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.detail}</span>
                    </div>
                    {item.event ? (
                      <a href={`/console/ranges/${encodeURIComponent(rangeId)}/apps/events?eventId=${encodeURIComponent(item.event.eventId)}#events-roster`}>
                        Attendance
                      </a>
                    ) : (
                      <span className="org-insights-journey-kind">{item.type === "reservation" ? reservationStatusLabel(item.reservation) : statusLabel(item.type)}</span>
                    )}
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <>
              {insightsMemberSearchLoading && <div className="status-strip">Loading members...</div>}
              {insightsMemberSearchError && <div className="alert alert-warning">{insightsMemberSearchError}</div>}
              {useLimitedMemberSearch && insightsMemberSearchSummary?.truncated && (
                <div className="status-strip">
                  Showing the closest {numberLabel(rangeMemberJourneyList.length, "member match", "member matches")}. Search by name or email to narrow the list.
                </div>
              )}
              {rangeMemberJourneyList.map((item) => (
                <button
                  type="button"
                  className="org-insights-member-row"
                  key={memberKey(item.member)}
                  onClick={() => setSelectedMemberKey(memberKey(item.member))}
                >
                  <div>
                    <strong>{memberName(item.member)}</strong>
                    <span>{item.member.email}</span>
                  </div>
                  <div>
                    <span>{statusLabel(item.status)}</span>
                    <small>Joined {formatDate(item.joinedAt)}</small>
                  </div>
                  <div>
                    <strong>{item.completedVisits}</strong>
                    <small>Completed visits</small>
                  </div>
                  <div>
                    <span>{item.lastReservation ? resourceName(item.lastReservation) : "No reservation yet"}</span>
                    <small>{item.lastVisitAt ? `Last visit ${formatDate(item.lastVisitAt)}` : item.activeReservations ? `${numberLabel(item.activeReservations, "future reservation")}` : "Open member journey"}</small>
                  </div>
                </button>
              ))}
            </>
          )}
          {rangeMemberJourneyList.length === 0 && !insightsMemberSearchLoading && <div className="org-insights-empty">No members match this view.</div>}
        </div>
      )}
    </section>
  );
}
